import { useCallback, useEffect, useRef } from 'react';
import type { Entity } from '../types';
import type { GoodsData } from '@banhala/ui-kit';
import type { FlatListProps } from 'react-native';
import { bridge, isNotNil } from '../../../../../utils';
import { AdAction } from '../../../../../utils/eventLogger/adLogger/types';
import { useLogAnalyticsEvent } from '../../../../../hooks/useLogAnalyticsEvent';
import { useIsForegroundNow } from '../../../../../hooks/useIsForegroundNow';
import { useOnForeground } from '../../../../../hooks/useOnForeground';

type ViewableItem = Parameters<
  NonNullable<FlatListProps<GoodsData & Entity>['onViewableItemsChanged']>
>[0]['viewableItems'][number];

/**
 * @param parentIsVisible carousel을 감싸는 부모(AtomicRenderer)의 viewport 가시성.
 * inner FlatList의 onViewableItemsChanged는 자체(가로) viewport 기준이라 부모가
 * carousel을 clip해도 전송되므로 emit 시점에 부모 가시성을 한 번 더 가드한다.
 * parentIsVisible 이 false 면 inner viewable 여부와 관계없이 차단하며,
 * false → true 전이 시 캐시된 viewable 아이템을 재전송해 카루셀이 막 시야에
 * 들어왔는데 자체 콜백이 안 도는 케이스(데이터 동일/재 mount 직후 등)를 보정한다.
 * @param isAwaitingDataChange AtomicRenderer가 필터 변경/pull-to-refresh 직후
 * 1.5초간 emit을 차단하는 윈도우. 데이터 swap + auto-scroll 애니메이션 도중
 * 일시적으로 parentIsVisible이 truthy가 되며 발생하는 spurious emit을 막기 위함.
 */
export const useLogDisplayGoods = (
  parentIsVisible?: boolean,
  isAwaitingDataChange?: boolean,
) => {
  const loggedSnos = useRef<Array<number>>([]);
  const lastViewableItemsRef = useRef<Array<ViewableItem>>([]);
  const isAwaitingDataChangeRef = useRef(isAwaitingDataChange);
  isAwaitingDataChangeRef.current = isAwaitingDataChange;
  // 화면이 실제 foreground인지(상세 push 등으로 가려지지 않았는지) 직접 확인.
  // hasViewable 게이트 우회가 background 상태에서 미리 emit하는 것을 막는 데 쓴다.
  const isForeground = useIsForegroundNow();
  const isForegroundRef = useRef(isForeground);
  isForegroundRef.current = isForeground;

  // bridge 직접 호출 대신 AnalyticsContext 자동 머지 wrapper 사용.
  // AnalyticsScreen이 주입한 SCREEN 등 공용 logging 필드가 누락되던 문제 해결.
  const logAnalyticsEvent = useLogAnalyticsEvent();

  const emitForViewable = useCallback(
    (viewableItems: Array<ViewableItem>) => {
      // 부모 viewport 가시성이 명시적 false 면 inner viewable 여부와 무관하게 차단.
      // parentIsVisible false → true 전환 시 useEffect 의 cache replay 로 노출 결손 방지.
      // undefined (default) 는 통과 — 호출처가 prop 을 안 넘기는 케이스(테스트/구컴포넌트) 대응.
      if (parentIsVisible === false) return;
      // viewable 아이템이 0이면 진짜 안 보이는 상태로 간주해 기존 게이트 유지.
      // isViewable=true가 1개 이상이면 inner FlatList가 활성 = cell이 viewport 근처에
      // 있다는 RN-native 신호 → 부모 visibility 판정이 stale일 가능성이 높으므로 게이트 우회.
      const hasViewable = viewableItems.some(v => v.isViewable);
      // 화면이 실제 foreground가 아니면(상세 push 등으로 가려짐) inner FlatList가
      // viewable을 보고해도 emit하지 않는다. 이 가드가 없으면 hasViewable 우회가
      // background에서도 통과해 이탈 시점에 미리 찍히고, 복귀 시 loggedSnos dedup
      // 으로 누락되는 문제가 생긴다.
      if (!isForegroundRef.current) return;
      if (!parentIsVisible && !hasViewable) return;
      if (isAwaitingDataChangeRef.current === true) return;
      viewableItems.forEach(({ isViewable, item }) => {
        if (!isViewable || item.sno == null) return;
        if (loggedSnos.current.includes(item.sno)) return;
        loggedSnos.current.push(item.sno);

        logAnalyticsEvent({
          channel: ['firebase', 'amplitude', 'ablylog', 'facebook'],
          name: 'DISPLAY_GOODS',
          analytics: item.logging.analytics,
          ably_only_log: item.logging.ably_only_log,
        });
        if (isNotNil(item.item.ad)) {
          bridge.logAdEvent({ action: AdAction.DISPLAY, ad: item.item.ad });
        }
      });
    },
    [logAnalyticsEvent, parentIsVisible],
  );

  // 부모 가시성이 truthy로 바뀌는 시점 / awaiting 윈도우가 풀리는 시점에
  // 캐시 기반 재평가 → loggedSnos dedup으로 중복 emit 없이 시야 진입 시점의
  // visible 아이템만 보냄.
  useEffect(() => {
    if (parentIsVisible && !isAwaitingDataChange) {
      emitForViewable(lastViewableItemsRef.current);
    }
  }, [parentIsVisible, isAwaitingDataChange, emitForViewable]);

  // 네이티브 화면 복귀(foreground false→true 전이) 시 캐시된 viewable 아이템을
  // 재평가. parentIsVisible 전이가 없는(이미 visible인 채 복귀하는) 케이스도
  // 커버한다. loggedSnos dedup으로 중복 emit 없이 누락분만 보낸다.
  const replayOnForeground = useCallback(
    () => emitForViewable(lastViewableItemsRef.current),
    [emitForViewable],
  );
  useOnForeground(replayOnForeground);

  const resetDisplayGoods = useCallback(() => {
    loggedSnos.current = [];
    emitForViewable(lastViewableItemsRef.current);
  }, [emitForViewable]);

  const logDisplayGoods = useCallback<
    NonNullable<FlatListProps<GoodsData & Entity>['onViewableItemsChanged']>
  >(
    ({ viewableItems }) => {
      lastViewableItemsRef.current = viewableItems;
      emitForViewable(viewableItems);
    },
    [emitForViewable],
  );

  return { logDisplayGoods, resetDisplayGoods };
};
